import type { Pregunta, ValorDeRespuesta } from './api';
import { Voz } from './Voz';

/**
 * Una pregunta en pantalla.
 *
 * ES UN COMPONENTE CONTROLADO: recibe el valor y avisa del cambio, no guarda
 * estado propio. Quien decide cuándo se escribe en el servidor es su padre, y
 * eso es lo que permite que responder y guardar sean dos cosas distintas — la
 * persona ve su respuesta marcada al instante aunque la red vaya lenta.
 *
 * SIN NINGÚN VALOR POR DEFECTO. Una escala no arranca en el punto medio y una
 * opción no viene preseleccionada: en un test psicométrico, un valor puesto por
 * la interfaz que la persona no llegó a mirar es un dato falso que puntúa igual
 * que uno verdadero.
 */

export interface PropiedadesDePregunta {
  pregunta: Pregunta;
  valor: ValorDeRespuesta | undefined;
  alResponder: (valor: ValorDeRespuesta) => void;
  /** Marca la pregunta como pendiente. Solo se enciende al intentar avanzar. */
  resaltarPendiente: boolean;
  /** La respuesta está en pantalla pero no llegó al servidor. */
  sinGuardar: boolean;
  /** Identificador del elemento, para poder desplazarse hasta esta pregunta. */
  id: string;
}

export function PreguntaEnPantalla({
  pregunta,
  valor,
  alResponder,
  resaltarPendiente,
  sinGuardar,
  id,
}: PropiedadesDePregunta) {
  const idAyuda = `${pregunta.id}-ayuda`;
  const pendiente = resaltarPendiente && pregunta.obligatoria && valor === undefined;

  /**
   * «Respondida» es tener valor, sin mirar qué valor. Es LA MISMA REGLA QUE EL
   * SERVIDOR, donde `progresoDe` cuenta identificadores y no contenido: un texto
   * que quedó vacío cuenta como respondido en los dos lados. Endurecerlo aquí
   * marcaría en rojo una pregunta que el servidor sí da por hecha.
   *
   * Lo que NO se marca como respondida es la que no llegó a guardarse: en
   * pantalla se ve la respuesta elegida, pero decir «hecho» de algo que el
   * servidor no tiene sería mentir justo donde más caro sale.
   */
  const clases = [
    'pregunta',
    sinGuardar ? 'pregunta--sin-guardar' : valor !== undefined ? 'pregunta--respondida' : '',
    pendiente ? 'pregunta--pendiente' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <fieldset className={clases} id={id}>
      <legend className="pregunta__enunciado">
        {pregunta.enunciado}
        {!pregunta.obligatoria && <span className="pregunta__opcional"> (opcional)</span>}
      </legend>

      {pregunta.ayuda && (
        <p className="pregunta__ayuda" id={idAyuda}>
          {pregunta.ayuda}
        </p>
      )}

      {pregunta.tipo === 'escala' && (
        <Escala pregunta={pregunta} valor={valor} alResponder={alResponder} idAyuda={idAyuda} />
      )}

      {pregunta.tipo === 'texto' && (
        <Texto pregunta={pregunta} valor={valor} alResponder={alResponder} idAyuda={idAyuda} />
      )}

      {pregunta.tipo === 'opcion_unica' && (
        <OpcionUnica pregunta={pregunta} valor={valor} alResponder={alResponder} />
      )}

      {pregunta.tipo === 'opcion_multiple' && (
        <OpcionMultiple pregunta={pregunta} valor={valor} alResponder={alResponder} />
      )}

      {pendiente && (
        <p className="pregunta__aviso" role="status">
          Falta responder esta.
        </p>
      )}

      {sinGuardar && (
        <p className="pregunta__aviso" role="status">
          Esta respuesta todavía no se ha guardado.
        </p>
      )}
    </fieldset>
  );
}

/**
 * La escala, como botones y no como un deslizador.
 *
 * Un deslizador obliga a apuntar y arrastrar en un móvil, y —peor— nace en una
 * posición: parece respondido antes de tocarlo. Con botones, «sin responder» se
 * ve distinto de cualquier respuesta.
 *
 * Cada botón lleva su número y su etiqueta cuando la tiene. Las etiquetas son lo
 * que da sentido a la escala («Totalmente falso» … «Me describe perfectamente»),
 * y esconderlas detrás de los extremos obligaría a deducir qué significa un 4.
 */
function Escala({
  pregunta,
  valor,
  alResponder,
  idAyuda,
}: {
  pregunta: Extract<Pregunta, { tipo: 'escala' }>;
  valor: ValorDeRespuesta | undefined;
  alResponder: (valor: ValorDeRespuesta) => void;
  idAyuda: string;
}) {
  const elegido = valor?.tipo === 'escala' ? valor.valor : undefined;
  const valores: number[] = [];
  for (let v = pregunta.minimo; v <= pregunta.maximo; v += 1) valores.push(v);

  const etiquetaDe = (v: number): string | undefined =>
    pregunta.etiquetas.find((e) => e.valor === v)?.texto;

  return (
    <div
      className="escala"
      role="radiogroup"
      aria-describedby={pregunta.ayuda ? idAyuda : undefined}
    >
      {valores.map((v) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={elegido === v}
          className={`escala__opcion${elegido === v ? ' escala__opcion--elegida' : ''}`}
          onClick={() => {
            alResponder({ tipo: 'escala', valor: v });
          }}
        >
          <span className="escala__numero">{v}</span>
          {etiquetaDe(v) && <span className="escala__etiqueta">{etiquetaDe(v)}</span>}
        </button>
      ))}
    </div>
  );
}

function Texto({
  pregunta,
  valor,
  alResponder,
  idAyuda,
}: {
  pregunta: Extract<Pregunta, { tipo: 'texto' }>;
  valor: ValorDeRespuesta | undefined;
  alResponder: (valor: ValorDeRespuesta) => void;
  idAyuda: string;
}) {
  const escrito = valor?.tipo === 'texto' ? valor.texto : '';
  const alCambiar = (texto: string): void => {
    alResponder({ tipo: 'texto', texto });
  };

  if (pregunta.extension === 'corta') {
    return (
      <input
        type="text"
        className="campo"
        value={escrito}
        maxLength={pregunta.maximo}
        aria-describedby={pregunta.ayuda ? idAyuda : undefined}
        onChange={(evento) => {
          alCambiar(evento.target.value);
        }}
      />
    );
  }

  return (
    <>
      {/*
       * ARRIBA DE LA CASILLA Y NO DEBAJO. Es la salida que hay que ver primero
       * en una pregunta que pide contar la propia historia: escribir eso en el
       * teclado de un móvil sale en tres líneas. Pero la casilla sigue justo
       * debajo, siempre, para quien no pueda o no quiera hablar.
       */}
      {pregunta.admiteVoz === true && (
        <Voz
          preguntaId={pregunta.id}
          hayEscrito={escrito.trim() !== ''}
          alTranscribir={alCambiar}
        />
      )}

      <textarea
        className="campo campo--area"
        rows={5}
        value={escrito}
        maxLength={pregunta.maximo}
        aria-describedby={pregunta.ayuda ? idAyuda : undefined}
        onChange={(evento) => {
          alCambiar(evento.target.value);
        }}
      />
    </>
  );
}

function OpcionUnica({
  pregunta,
  valor,
  alResponder,
}: {
  pregunta: Extract<Pregunta, { tipo: 'opcion_unica' }>;
  valor: ValorDeRespuesta | undefined;
  alResponder: (valor: ValorDeRespuesta) => void;
}) {
  const elegida = valor?.tipo === 'opcion_unica' ? valor.opcionId : undefined;

  return (
    <div className="opciones" role="radiogroup">
      {pregunta.opciones.map((opcion) => (
        <button
          key={opcion.id}
          type="button"
          role="radio"
          aria-checked={elegida === opcion.id}
          className={`opcion${elegida === opcion.id ? ' opcion--elegida' : ''}`}
          onClick={() => {
            alResponder({ tipo: 'opcion_unica', opcionId: opcion.id });
          }}
        >
          {/* Decorativa: quien la anuncia es el `aria-checked` del botón, y
              repetirla por voz sería decir dos veces lo mismo. */}
          <span className="opcion__marca opcion__marca--una" aria-hidden="true" />
          {opcion.texto}
        </button>
      ))}
    </div>
  );
}

function OpcionMultiple({
  pregunta,
  valor,
  alResponder,
}: {
  pregunta: Extract<Pregunta, { tipo: 'opcion_multiple' }>;
  valor: ValorDeRespuesta | undefined;
  alResponder: (valor: ValorDeRespuesta) => void;
}) {
  const elegidas = valor?.tipo === 'opcion_multiple' ? valor.opcionIds : [];

  return (
    <div className="opciones">
      {pregunta.opciones.map((opcion) => {
        const marcada = elegidas.includes(opcion.id);
        return (
          <button
            key={opcion.id}
            type="button"
            role="checkbox"
            aria-checked={marcada}
            className={`opcion${marcada ? ' opcion--elegida' : ''}`}
            onClick={() => {
              alResponder({
                tipo: 'opcion_multiple',
                opcionIds: marcada
                  ? elegidas.filter((id) => id !== opcion.id)
                  : [...elegidas, opcion.id],
              });
            }}
          >
            {/* Redonda para elegir una, cuadrada para elegir varias: es lo único
                que distingue las dos listas antes de tocarlas. */}
            <span className="opcion__marca opcion__marca--varias" aria-hidden="true" />
            {opcion.texto}
          </button>
        );
      })}
    </div>
  );
}
